const AMOUNT_TOKEN_REGEX = /\{\{\s*([\w_]+)\s*\}\}/;

/** Shopify money_format may wrap tokens in theme HTML (e.g. <span class="money">). */
function normalizeMoneyFormatTemplate(template) {
  return String(template || "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "ISK",
  "JPY",
  "KMF",
  "KRW",
  "PYG",
  "RWF",
  "UGX",
  "UYI",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF",
]);

function addThousandsSeparator(value, separator) {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

function resolveFractionDigits(currency, maximumFractionDigits) {
  if (typeof maximumFractionDigits === "number") {
    return Math.max(0, maximumFractionDigits);
  }
  return ZERO_DECIMAL_CURRENCIES.has(String(currency || "").toUpperCase()) ? 0 : 2;
}

function formatAmountToken(tokenName, amount, fractionDigits, currency) {
  const safeAmount = Number(amount);
  const numericAmount = Number.isFinite(safeAmount) ? safeAmount : 0;
  const usesDecimals = fractionDigits > 0;

  const numberStr = usesDecimals
    ? numericAmount.toFixed(fractionDigits)
    : String(Math.round(numericAmount));

  const [intPart, decimalPart = "00"] = numberStr.split(".");

  switch (tokenName) {
    case "amount_no_decimals":
      return addThousandsSeparator(Math.round(numericAmount), ",");
    case "amount_with_comma_separator":
      return usesDecimals
        ? `${addThousandsSeparator(intPart, ".")},${decimalPart}`
        : addThousandsSeparator(Math.round(numericAmount), ".");
    case "amount_no_decimals_with_comma_separator":
      return addThousandsSeparator(Math.round(numericAmount), ".");
    case "amount_with_apostrophe_separator":
      return usesDecimals
        ? `${addThousandsSeparator(intPart, "'")}.${decimalPart}`
        : addThousandsSeparator(Math.round(numericAmount), "'");
    case "amount_no_decimals_with_space_separator":
      return addThousandsSeparator(Math.round(numericAmount), " ");
    case "amount_with_space_separator":
      return usesDecimals
        ? `${addThousandsSeparator(intPart, " ")},${decimalPart}`
        : addThousandsSeparator(Math.round(numericAmount), " ");
    case "amount_with_period_and_space_separator":
      return usesDecimals
        ? `${addThousandsSeparator(intPart, " ")}.${decimalPart}`
        : addThousandsSeparator(Math.round(numericAmount), " ");
    case "amount_with_currency":
      return formatWithIntl(numericAmount, currency, fractionDigits);
    case "amount":
    default:
      return usesDecimals
        ? `${addThousandsSeparator(intPart, ",")}.${decimalPart}`
        : addThousandsSeparator(Math.round(numericAmount), ",");
  }
}

function formatWithIntl(amount, currency, maximumFractionDigits) {
  const fractionDigits = resolveFractionDigits(currency, maximumFractionDigits);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(amount);
  } catch {
    return `${currency || "USD"} ${Number(amount).toFixed(fractionDigits)}`;
  }
}

/**
 * Format money using Shopify's money template (e.g. "€{{amount_with_comma_separator}}", "{{amount}} BD").
 */
export function formatShopMoney(amount, options = {}) {
  const {
    moneyFormat,
    currency = "USD",
    round = false,
    maximumFractionDigits,
  } = options;

  let numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) return "—";
  if (round) numericAmount = Math.round(numericAmount);

  const fractionDigits = resolveFractionDigits(currency, maximumFractionDigits);
  const template = normalizeMoneyFormatTemplate(moneyFormat);

  if (!template) {
    return formatWithIntl(numericAmount, currency, fractionDigits);
  }

  const tokenMatch = template.match(AMOUNT_TOKEN_REGEX);
  if (!tokenMatch) {
    return formatWithIntl(numericAmount, currency, fractionDigits);
  }

  const formattedAmount = formatAmountToken(
    tokenMatch[1],
    numericAmount,
    fractionDigits,
    currency
  );

  return template.replace(AMOUNT_TOKEN_REGEX, formattedAmount);
}

export function getShopMoneyFormatOptions(shopData) {
  return {
    currency: shopData?.currency || "USD",
    moneyFormat: shopData?.moneyFormat || shopData?.money_format || "",
  };
}

export function formatShopProductPrice(amount, shopData) {
  return formatShopMoney(amount, getShopMoneyFormatOptions(shopData));
}
